import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { backupJson, planMigration, type LegacyItem } from '../office/materialLibrary';
import { runMigration } from '../office/materialLibraryStore';

const BORDER = '#e8e8e8';
const GOLD_D = '#7a5525';
const MUTED = '#6b6b6b';

/**
 * Shown to an admin while the rate library still lives only inside this project. Moves it into the
 * shared library (keeping every id) in three steps: back it up, preview it, run it.
 */
export default function RateLibraryMigration({ projectId, legacyItems, onDone }: {
  projectId: string;
  legacyItems: LegacyItem[];
  onDone?: (materials: number) => void;
}) {
  const plan = useMemo(() => planMigration(legacyItems, new Set()), [legacyItems]);
  const [backedUp, setBackedUp] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState('');

  const downloadBackup = () => {
    const blob = new Blob([backupJson(projectId, legacyItems)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rate-library-backup-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setBackedUp(true);
  };

  const run = async () => {
    if (!window.confirm(`Move ${plan.materials} material${plan.materials === 1 ? '' : 's'} into the shared rate library? Every id stays the same, and the old copy is kept read-only.`)) return;
    setError(''); setProgress({ done: 0, total: plan.materials });
    try {
      await runMigration(projectId, plan, (done, total) => setProgress({ done, total }));
      onDone?.(plan.materials);
    } catch (err: any) {
      setError(err?.message || 'The migration could not finish. Nothing already copied is lost; you can run it again.');
      setProgress(null);
    }
  };

  return <Box sx={{ mb: 2, border: `1px solid #e2c982`, bgcolor: '#fff8e6', borderRadius: 2, p: 2 }}>
    <Typography sx={{ fontWeight: 900, color: GOLD_D }}>Share this rate library with all projects</Typography>
    <Typography sx={{ color: MUTED, fontSize: 13, mt: 0.5 }}>
      The rate library is still stored inside this project. Moving it to the shared library gives every project the same materials and ids.
      Vendor quotes become shared too, so a rate entered once is seen by every project. Each project keeps its own selected vendors, deal notes and freight. Estimates, orders and bills are not affected, because every material id stays the same.
      Until this is done the library is read-only.
    </Typography>
    <Box sx={{ mt: 1.25, p: 1.25, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: 1.5 }}>
      <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Preview</Typography>
      <Typography sx={{ color: MUTED, fontSize: 13 }}>{plan.materials} material{plan.materials === 1 ? '' : 's'} · {plan.quotes} vendor quote{plan.quotes === 1 ? '' : 's'} · {plan.withNotes} with deal notes. Nothing is deleted; the old copy stays as a read-only backup.</Typography>
    </Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ mt: 1.25 }} alignItems={{ sm: 'center' }}>
      <Button variant="outlined" onClick={downloadBackup} sx={{ borderColor: GOLD_D, color: GOLD_D }}>1. Download backup</Button>
      <Button variant="contained" disabled={!backedUp || !plan.materials || !!progress} onClick={run}>2. Move to shared library</Button>
      {!backedUp && <Typography sx={{ color: MUTED, fontSize: 12 }}>Download the backup first to enable the move.</Typography>}
    </Stack>
    {progress && <Box sx={{ mt: 1.25 }}><LinearProgress variant="determinate" value={progress.total ? (progress.done / progress.total) * 100 : 100} /><Typography sx={{ color: MUTED, fontSize: 12, mt: 0.5 }}>Moved {progress.done} of {progress.total}</Typography></Box>}
    {error && <Alert severity="error" sx={{ mt: 1.25 }}>{error}</Alert>}
  </Box>;
}
